import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Toggle from 'react-toggle';
import { FormattedMessage } from 'react-intl';
import OkCancelModal from '@/shared/components/OkCancelModal';
import classNames from 'classnames';
import DataCacheService from '@/shared/services/DataCacheService';
import api from '@/shared/api';
import config from '@/config';
import { dynamicSort, dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import DatePicker from '@/shared/components/input/DatePicker';
import handleHttpError from '@/shared/services/HandleHttpError';
import intlShape from '@/shared/utils/intlShape';
import ToastrService from '@/shared/services/ToastrService';
import { injectStateService, StateService } from '@/shared/services/StateService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import RelationSearch from '@/shared/components/input/RelationSearch';
import AutoComplete from '@/shared/components/input/select/AutoComplete';
import { falseFunc, isBeforeMinimum } from '@/shared/utils/validationHelpers';
import messages from './priceGridMessages';

class RecalcRegistrationPriceModal extends Component {
  state = {
    filters: {
      priceGridId: null,
      priceProfileId: null,
      relationId: null,
      userId: null,
      companyId: null,
      fromDate: moment.utc().startOf('day'),
      overrideManualPrices: false,
      resourceCategoryId: null,
    },
    selectedRelation: null,
    users: [],
    priceGrids: [],
    companies: [],
    registrationCount: null,
    dateFocused: null,
  };
  static propTypes = {
    intl: intlShape.isRequired,
    gridId: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    profiles: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ).isRequired,
    stateService: PropTypes.instanceOf(StateService).isRequired,
  };

  static defaultProps = {
    gridId: null,
  };

  componentDidMount() {
    const { intl, gridId } = this.props;

    const dataCache = DataCacheService.getDataCache();
    if (!dataCache) return;
    Promise.all([
      api.base.get(`${config.appUrls.api}/api/resource/registration`),
      api.base.get(`${config.appUrls.api}/api/Price/Grid`),
    ]).then(([{ data: categories = [] }, { data: grids = [] }]) => {
      const allResourceCategories = { id: null, label: intl.formatMessage(messages.AllResources) };

      const priceGrids = grids
        .map(x => ({
          ...x,
          name: x.name === 'Default' ? intl.formatMessage(messages.Default) : x.name,
        }))
        .sort(dynamicSortMultiple('-isDefault', 'name'));
      const resourceCategories = [
        allResourceCategories,
        ...categories
          .map(x => {
            const codePrefix = x.code ? `${x.code} – ` : '';

            const label = `${codePrefix}${intl.formatMessage(
              listItemMsg('resource', x.id, x.name),
            )}`;
            return { id: x.categoryId, label };
          })
          .sort(dynamicSort('label')),
      ];

      const companies = dataCache.company;

      this.setState(state => ({
        priceGrids,
        users: DataCacheService.getAllUsers(),
        companies,
        resourceCategories,
        filters: {
          ...state.filters,
          priceGridId: gridId,
        },
      }));
    });
  }

  updateFilter = filter => {
    this.setState(
      state => ({ filters: { ...state.filters, ...filter } }),
      () => {
        this.retrieveCount();
      },
    );
  };
  relationChanged = relation => {
    this.setState(
      state => ({
        filters: { ...state.filters, relationId: relation && relation.id ? relation.id : null },
        selectedRelation: relation || null,
      }),
      () => {
        this.retrieveCount();
      },
    );
  };

  resourceChanged = resource => {
    this.setState(
      state => ({
        filters: {
          ...state.filters,
          resourceCategoryId: resource && resource.id ? resource.id : null,
        },
        selectedResourceCategory: resource || null,
      }),
      () => {
        this.retrieveCount();
      },
    );
  };

  validate = () => {
    const { selectedResourceCategory } = this.state;
    const invalidResourceCategory = !selectedResourceCategory;
    this.setState({ invalidResourceCategory });
    return !invalidResourceCategory;
  };

  retrieveCount = () => {
    const { filters } = this.state;
    if (!this.validate()) return;

    return this.setState({ retrievingCount: true }, () => {
      return api.base
        .post(`${config.appUrls.api}/Api/Registration/RecalcPriceCount`, filters)
        .then(({ data = null }) => {
          this.setState({ registrationCount: data, retrievingCount: false });
        });
    });
  };

  onSave = () => {
    const { filters, retrievingCount, registrationCount } = this.state;
    const { onClose, stateService, intl } = this.props;
    if (!this.validate()) return;
    if (retrievingCount || registrationCount === null || registrationCount === 0) return;

    return api.base
      .post(`${config.appUrls.api}/Api/Registration/RecalcPrice`, filters)
      .then(({ data = null }) => {
        if (data) {
          const onClick = data.id ? () => stateService.gotoJob(data.id) : undefined;

          ToastrService.info(
            intl.formatMessage(messages.RecalcRegistrationPriceJobQueued),
            undefined,
            onClick,
          );
        }
        onClose();
      }, handleHttpError(intl));
  };

  render() {
    const { onClose, intl, profiles } = this.props;
    const {
      filters,
      companies,
      users,
      priceGrids,
      registrationCount,
      dateFocused,
      resourceCategories,
      selectedRelation,
      selectedResourceCategory,
      invalidResourceCategory,
    } = this.state;

    const hasMultipleCompanies = !!companies && companies.length > 1;

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.RecalculateRegistrationPrices)}
        show
        onClose={onClose}
        onSave={() => this.onSave()}
      >
        <div className="form-horizontal">
          <div
            className={classNames('form-group', { 'has-error': isBeforeMinimum(filters.fromDate) })}
          >
            <label className="control-label col-sm-4">
              {intl.formatMessage(messages.RegistrationDateFrom)}
            </label>
            <div className="col-sm-5">
              <DatePicker
                placeholder={intl.formatMessage(messages.All)}
                date={filters.fromDate}
                onDateChange={date => this.updateFilter({ fromDate: date })}
                focused={dateFocused}
                onFocusChange={({ focused }) => this.setState({ dateFocused: focused })}
                id="fromDate"
                block
                small
                showClearDate
                isOutsideRange={falseFunc}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="control-label col-sm-4">
              {intl.formatMessage(messages.PriceGrid)}
            </label>
            <div className="col-sm-5">
              {priceGrids && (
                <select
                  onChange={e => this.updateFilter({ priceGridId: e.target.value || null })}
                  className="form-control"
                  value={filters.priceGridId || ''}
                >
                  <option value="">{intl.formatMessage(messages.AllPriceGrids)}</option>
                  {priceGrids.map(grid => (
                    <option key={grid.id} value={grid.id}>
                      {grid.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div className="form-group">
            <label className="control-label col-sm-4">
              {intl.formatMessage(messages.PriceProfile)}
            </label>
            <div className="col-sm-5">
              {profiles && (
                <select
                  onChange={e => this.updateFilter({ priceProfileId: e.target.value || null })}
                  className="form-control"
                  value={filters.priceProfileId || ''}
                >
                  <option value="">{intl.formatMessage(messages.AllProfiles)}</option>
                  {profiles.map(profile => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div className={classNames('form-group', { 'has-error': invalidResourceCategory })}>
            <label className="control-label col-sm-4">
              {intl.formatMessage(messages.ResourceCategory)}
            </label>
            <div className="col-sm-5">
              {resourceCategories && (
                <AutoComplete
                  items={resourceCategories}
                  onChange={this.resourceChanged}
                  mode="edit"
                  value={selectedResourceCategory}
                  showAll
                />
              )}
            </div>
          </div>
          <div className="form-group">
            <label className="control-label col-sm-4">{intl.formatMessage(messages.User)}</label>
            <div className="col-sm-5">
              {users && (
                <select
                  onChange={e => this.updateFilter({ userId: e.target.value || null })}
                  className="form-control"
                  value={filters.userId || ''}
                >
                  <option value="">{intl.formatMessage(messages.AllUsers)}</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div className="form-group">
            <label className="control-label col-sm-4">
              {intl.formatMessage(messages.Relation)}
            </label>
            <div className="col-sm-5">
              <RelationSearch
                onChange={this.relationChanged}
                initialSelectedItem={selectedRelation}
              />
            </div>
          </div>
          {hasMultipleCompanies && (
            <div className="form-group">
              <label className="control-label col-sm-4">
                {intl.formatMessage(messages.OwnCompany)}
              </label>
              <div className="col-sm-5">
                <select
                  onChange={e => this.updateFilter({ companyId: e.target.value || null })}
                  className="form-control"
                  value={filters.companyId || ''}
                >
                  <option value="">{intl.formatMessage(messages.AllCompanies)}</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div className="form-group">
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.OverrideManuallyChangedPrices} />
            </label>
            <div className="col-sm-5">
              <div className="react-toggle-wrapper margin10T">
                <Toggle
                  checked={filters.overrideManualPrices}
                  onChange={e => this.updateFilter({ overrideManualPrices: e.target.checked })}
                />
              </div>
            </div>
          </div>
          {registrationCount !== null && (
            <div className="alert alert-info">
              <FormattedMessage
                {...messages.XRegistrationsWillBeUpdated}
                values={{ x: registrationCount }}
              />
            </div>
          )}
        </div>
      </OkCancelModal>
    );
  }
}

export default injectStateService(RecalcRegistrationPriceModal);
